import React from "react";
import { Link } from "react-router-dom";
import {
  X,
  HelpCircle,
  Linkedin,
  LogOut,
  Mail,
  Globe,
  Info,
  User2,
  Moon,
  Sun,
  Code,
  User,
  Users,
  CreditCard,
  Plug,
  Shield,
  FileText,
} from "lucide-react";
import { AppConfig } from "../../utils/Constants";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { isProduction } from "../../utils/Environment";

interface Tab {
  key: string;
  label: string;
  icon: React.ElementType;
  devOnly: boolean;
  hidden?: boolean;
}

interface ResourceLink {
  key: string;
  label: string;
  description: string;
  icon: React.ElementType;
  href: string;
  onClick?: (onTabChange: (tab: string) => void) => void;
}

interface LeftPanelProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  onLogoutClick: () => void;
  onCloseSettings: () => void;
}

const tabs: Tab[] = [
  { key: "profile", label: "Profile Settings", icon: User, devOnly: false },
  {
    key: "members",
    label: "Organization",
    icon: Users,
    devOnly: false,
  },
  // { key: "billing", label: "Billing", icon: CreditCard, devOnly: false }, // Hidden - will be brought back in the future
  {
    key: "integrations",
    label: "Connectors",
    icon: Plug,
    devOnly: false,
  },
];

const resourceLinks: ResourceLink[] = [
  {
    key: "support",
    label: "Support",
    description: "Get help with any issues",
    icon: HelpCircle,
    href: `mailto:${AppConfig.SUPPORT_EMAIL}`,
  },
  {
    key: "privacy",
    label: "Privacy Policy",
    description: "How we protect your data",
    icon: Shield,
    href: "/privacy",
  },
  {
    key: "terms",
    label: "Terms & Conditions",
    description: "Service terms and conditions",
    icon: FileText,
    href: "/terms",
  },
];

const companyLinks = [
  {
    key: "website",
    label: "Services Page",
    icon: Globe,
    href: AppConfig.PRODUCT_SERVICES_URL,
  },
  {
    key: "linkedin",
    label: "LinkedIn",
    icon: Linkedin,
    href: AppConfig.LINKEDIN_URL,
  },
  {
    key: "contact",
    label: "Support",
    icon: Mail,
    href: `mailto:${AppConfig.SUPPORT_EMAIL}`,
  },
];

const LeftPanel: React.FC<LeftPanelProps> = ({
  activeTab,
  onTabChange,
  onLogoutClick,
  onCloseSettings,
}) => {
  const isProd = isProduction;
  const { userProfile } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const navItemClasses = (tabKey: string) => {
    const isActive = activeTab === tabKey;
    const isDevOnly = tabs.find((tab) => tab.key === tabKey)?.devOnly;
    const isDisabled = isProd && isDevOnly;

    return `
      relative flex items-center gap-2.5
      px-3 py-2
      rounded-md
      ${isDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}
      text-sm font-medium
      transition-colors duration-150
      ${
        isActive
          ? "bg-black dark:bg-white text-white dark:text-black"
          : "hover:bg-background-default dark:hover:bg-white/5 text-black/70 dark:text-white/70 hover:text-black dark:hover:text-white"
      }
    `;
  };

  const handleTabChange = (tabKey: string) => {
    const isDevOnly = tabs.find((tab) => tab.key === tabKey)?.devOnly;
    if (isProd && isDevOnly) {
      return;
    }
    onTabChange(tabKey);
  };

  return (
    <aside className="w-[220px] bg-background-panel dark:bg-background-panelDark text-text-primary dark:text-white flex flex-col h-full">
      <div className="flex items-center justify-between px-4 pt-6 pb-3 border-b border-divider dark:border-white/10">
        <h2 className="text-base font-semibold text-black dark:text-white">
          Settings
        </h2>
        <button
          onClick={onCloseSettings}
          aria-label="Close Settings"
          className="text-text-secondary dark:text-white/60 hover:text-text-primary dark:hover:text-white transition"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-hide">
        <div className="px-3 pt-4 pb-3">
          <h3 className="text-[10px] uppercase font-semibold tracking-wider text-black/50 dark:text-white/50 mb-2.5 px-1">
            Main Settings
          </h3>
          <div className="space-y-0.5">
            {tabs
              .filter((tab) => !tab.hidden)
              .map((tab) => (
                <div
                  key={tab.key}
                  className={navItemClasses(tab.key)}
                  onClick={() => handleTabChange(tab.key)}
                >
                  <tab.icon className="w-4 h-4 flex-shrink-0" />
                  <span>{tab.label}</span>
                </div>
              ))}
          </div>
        </div>

        <div className="px-3 py-3 border-t border-divider dark:border-white/10">
          <h3 className="text-[10px] uppercase font-semibold tracking-wider text-black/50 dark:text-white/50 mb-2.5 px-1">
            Preferences
          </h3>
          <div className="space-y-0.5">
            <div className="flex items-center px-3 py-2 rounded-md">
              {theme === "dark" ? (
                <Moon className="w-4 h-4 text-black/50 dark:text-white/50 flex-shrink-0" />
              ) : (
                <Sun className="w-4 h-4 text-black/50 dark:text-white/50 flex-shrink-0" />
              )}
              <span className="ml-2.5 text-sm text-black/70 dark:text-white/70">
                Theme
              </span>
              <div className="ml-auto">
                <button
                  onClick={toggleTheme}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none border ${
                    theme === "dark"
                      ? "bg-secondary border-secondary"
                      : "bg-background-panel border-divider"
                  }`}
                >
                  <span
                    className={`inline-block h-3.5 w-3.5 transform rounded-full transition-transform ${
                      theme === "dark"
                        ? "bg-white translate-x-[18px]"
                        : "bg-black translate-x-[2px]"
                    }`}
                  />
                </button>
              </div>
            </div>

            <div className="flex items-center px-3 py-2 rounded-md">
              <Code className="w-4 h-4 text-black/50 dark:text-white/50 flex-shrink-0" />
              <span className="ml-2.5 text-sm text-black/50 dark:text-white/50">
                v{AppConfig.VERSION}
              </span>
            </div>
          </div>
        </div>

        <div className="px-3 py-3 border-t border-divider dark:border-white/10">
          <h3 className="text-[10px] uppercase font-semibold tracking-wider text-black/50 dark:text-white/50 mb-2.5 px-1">
            Resources
          </h3>
          <div className="space-y-0.5">
            {resourceLinks.map((link) => {
              const isInternalLink = link.href.startsWith("/");
              const linkClasses = "flex items-center px-3 py-2 rounded-md hover:bg-background-default dark:hover:bg-white/5 transition-colors";
              
              if (isInternalLink) {
                return (
                  <Link
                    key={link.key}
                    to={link.href}
                    className={linkClasses}
                  >
                    <link.icon className="w-4 h-4 text-black/50 dark:text-white/50 flex-shrink-0" />
                    <span className="ml-2.5 text-sm text-black/70 dark:text-white/70">
                      {link.label}
                    </span>
                  </Link>
                );
              }
              
              return (
                <a
                  key={link.key}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={linkClasses}
                >
                  <link.icon className="w-4 h-4 text-black/50 dark:text-white/50 flex-shrink-0" />
                  <span className="ml-2.5 text-sm text-black/70 dark:text-white/70">
                    {link.label}
                  </span>
                </a>
              );
            })}
          </div>
        </div>
      </div>

      <div className="border-t border-divider dark:border-white/10">
        <div className="px-3 py-3">
          <div className="mb-3">
            <h3 className="text-[10px] uppercase font-semibold tracking-wider text-black/50 dark:text-white/50 mb-2.5 px-1">
              Connect
            </h3>
            <div className="space-y-0.5">
              {companyLinks.map((link) => (
                <a
                  key={link.key}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center px-3 py-2 rounded-md hover:bg-background-default dark:hover:bg-white/5 transition-colors"
                >
                  <link.icon className="w-4 h-4 text-black/50 dark:text-white/50 flex-shrink-0" />
                  <span className="ml-2.5 text-sm text-black/70 dark:text-white/70">
                    {link.label}
                  </span>
                </a>
              ))}
            </div>
          </div>

          <div className="border-t border-divider dark:border-white/10 pt-3">
            <div className="flex items-center px-3 py-2 rounded-md">
              <div
                className={`w-7 h-7 rounded-md flex items-center justify-center overflow-hidden flex-shrink-0 ${
                  userProfile?.avatar
                    ? "bg-transparent"
                    : "bg-background-panel dark:bg-white/10"
                }`}
              >
                {userProfile?.avatar ? (
                  <img
                    src={userProfile.avatar}
                    alt="Profile"
                    className="w-full h-full object-cover bg-transparent"
                  />
                ) : (
                  <User2 className="w-3.5 h-3.5 text-black/50 dark:text-white/50" />
                )}
              </div>
              <div className="ml-2.5 flex-1 min-w-0">
                <p className="text-sm font-medium text-black dark:text-white/90 truncate">
                  {userProfile?.firstName} {userProfile?.lastName}
                </p>
                <p className="text-xs text-black/50 dark:text-white/50 truncate">
                  {userProfile?.email || ""}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-3">
            <button
              onClick={onLogoutClick}
              className="w-full py-2 flex justify-center items-center gap-2 rounded-md hover:bg-background-default dark:hover:bg-white/5 transition-colors text-sm font-medium text-black/70 dark:text-white/70"
            >
              <LogOut className="w-4 h-4" />
              <span>Log Out</span>
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default LeftPanel;
